import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as Styled from 'events/styles/DaySummary.style';

const ARCH_BARS_DEFAULT = 28;
const ARCH_CHUNKY_THRESHOLD = 10;
const ARCH_STROKE_DEFAULT = 4;
const ARCH_CHUNKY_GAP_PX = 4;
const ARCH_CHUNKY_CORNER_R = 3;
const ARCH_VIEW_WIDTH = 220;
const ARCH_VIEW_HEIGHT = 110;
const ARCH_INNER_R = 70;
const ARCH_OUTER_R = 95;

interface ArchChartProps {
  resolvedCount: number;
  openCount: number;
}

export default function ArchChart({
  resolvedCount,
  openCount,
}: ArchChartProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const total = resolvedCount + openCount;
  const cx = ARCH_VIEW_WIDTH / 2;
  const cy = ARCH_VIEW_HEIGHT;

  const isChunky = total > 0 && total < ARCH_CHUNKY_THRESHOLD;

  const defaultSegments = useMemo(() => {
    if (isChunky) return [];
    const barCount =
      total === 0 ? ARCH_BARS_DEFAULT : Math.min(total, ARCH_BARS_DEFAULT);
    let resolvedBars: number;
    if (total === 0) {
      resolvedBars = 0;
    } else if (total > ARCH_BARS_DEFAULT) {
      resolvedBars = Math.round((resolvedCount / total) * barCount);
    } else {
      resolvedBars = resolvedCount;
    }

    return Array.from({ length: barCount }, (_, i) => {
      const t1 = barCount === 1 ? 0.5 : i / (barCount - 1);
      const angle = Math.PI + t1 * Math.PI;
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);

      let colour: string;
      if (total === 0) {
        colour = Styled.EMPTY_COLOUR;
      } else if (i < resolvedBars) {
        colour = Styled.RESOLVED_COLOUR;
      } else {
        colour = Styled.OPEN_COLOUR;
      }

      return {
        x1: cx + cos * ARCH_INNER_R,
        y1: cy + sin * ARCH_INNER_R,
        x2: cx + cos * ARCH_OUTER_R,
        y2: cy + sin * ARCH_OUTER_R,
        colour,
      };
    });
  }, [isChunky, resolvedCount, total, cx, cy]);

  const chunkySegments = useMemo(() => {
    if (!isChunky) return [];
    const pathInnerR = ARCH_INNER_R + ARCH_CHUNKY_CORNER_R;
    const pathOuterR = ARCH_OUTER_R - ARCH_CHUNKY_CORNER_R;
    const halfPathGap = ARCH_CHUNKY_GAP_PX / 2 + ARCH_CHUNKY_CORNER_R;
    const tInner = Math.sqrt(
      pathInnerR * pathInnerR - halfPathGap * halfPathGap
    );
    const tOuter = Math.sqrt(
      pathOuterR * pathOuterR - halfPathGap * halfPathGap
    );

    const slotAngle = Math.PI / total;

    return Array.from({ length: total }, (_, i) => {
      const aLeft = Math.PI + i * slotAngle;
      const aRight = Math.PI + (i + 1) * slotAngle;
      const cosL = Math.cos(aLeft);
      const sinL = Math.sin(aLeft);
      const cosR = Math.cos(aRight);
      const sinR = Math.sin(aRight);

      // Left side: parallel to radial at aLeft, shifted toward bar interior.
      const innerLeftX = cx + tInner * cosL - halfPathGap * sinL;
      const innerLeftY = cy + tInner * sinL + halfPathGap * cosL;
      const outerLeftX = cx + tOuter * cosL - halfPathGap * sinL;
      const outerLeftY = cy + tOuter * sinL + halfPathGap * cosL;
      // Right side: parallel to radial at aRight, shifted toward bar interior.
      const innerRightX = cx + tInner * cosR + halfPathGap * sinR;
      const innerRightY = cy + tInner * sinR - halfPathGap * cosR;
      const outerRightX = cx + tOuter * cosR + halfPathGap * sinR;
      const outerRightY = cy + tOuter * sinR - halfPathGap * cosR;

      const d = [
        `M ${innerLeftX.toFixed(2)} ${innerLeftY.toFixed(2)}`,
        `L ${outerLeftX.toFixed(2)} ${outerLeftY.toFixed(2)}`,
        `A ${pathOuterR} ${pathOuterR} 0 0 1 ${outerRightX.toFixed(
          2
        )} ${outerRightY.toFixed(2)}`,
        `L ${innerRightX.toFixed(2)} ${innerRightY.toFixed(2)}`,
        `A ${pathInnerR} ${pathInnerR} 0 0 0 ${innerLeftX.toFixed(
          2
        )} ${innerLeftY.toFixed(2)}`,
        'Z',
      ].join(' ');
      return {
        d,
        colour: i < resolvedCount ? Styled.RESOLVED_COLOUR : Styled.OPEN_COLOUR,
      };
    });
  }, [isChunky, resolvedCount, total, cx, cy]);

  return (
    <Styled.ArchWrapper>
      <Styled.ArchSvg
        viewBox={`0 0 ${ARCH_VIEW_WIDTH} ${ARCH_VIEW_HEIGHT + 5}`}
        aria-hidden
      >
        {isChunky
          ? chunkySegments.map((s, i) => (
              <path
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                d={s.d}
                fill={s.colour}
                stroke={s.colour}
                strokeWidth={ARCH_CHUNKY_CORNER_R * 2}
                strokeLinejoin="round"
              />
            ))
          : defaultSegments.map((s, i) => (
              <line
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                x1={s.x1}
                y1={s.y1}
                x2={s.x2}
                y2={s.y2}
                stroke={s.colour}
                strokeWidth={ARCH_STROKE_DEFAULT}
                strokeLinecap="round"
              />
            ))}
      </Styled.ArchSvg>
      <Styled.ArchCentre>
        {total === 0 ? (
          <Styled.EmptyMessage>
            {t('daySummary.emptyMessage')}
          </Styled.EmptyMessage>
        ) : (
          <>
            <Styled.ArchNumber>{total}</Styled.ArchNumber>
            <Styled.ArchLabel>{t('daySummary.events')}</Styled.ArchLabel>
          </>
        )}
      </Styled.ArchCentre>
    </Styled.ArchWrapper>
  );
}
